import { useCallback, useEffect, useMemo, useState } from "react";
import api, { hrs, eur, formatApiError } from "@/lib/apiClient";
import EmployeeLayout from "@/layouts/EmployeeLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { mondayOf, weekDays, shiftWeek } from "@/lib/week";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Plus, Trash2, Coffee, Copy, Receipt, Paperclip, Camera, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const shrinkPhoto = (file) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(img.src);
    c.toBlob((b) => resolve(b && b.size < file.size ? new File([b], (file.name || "receipt").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }) : file), "image/jpeg", 0.82);
  };
  img.onerror = () => resolve(file);
  img.src = URL.createObjectURL(file);
});
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { ClipboardList } from "lucide-react";
import { HoursSplit } from "@/components/RateBreakdown";

const EMPTY_ENTRY = { site_id: "", job_type: "General Electrical", start_time: "08:00", finish_time: "16:30", description: "", travel_minutes: 0, declaration: true };
const TRAVEL_H = [0, 1, 2, 3, 4, 5, 6];
const TRAVEL_M = [0, 15, 30, 45];
const fmtTravel = (m) => `${Math.floor(m / 60) ? `${Math.floor(m / 60)}h ` : ""}${m % 60 ? `${m % 60}m` : ""}`.trim();

export default function MyTimesheets() {
  const [monday, setMonday] = useState(mondayOf());
  const [ts, setTs] = useState(null);
  const [entries, setEntries] = useState([]);
  const [computed, setComputed] = useState(null);
  const [sites, setSites] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [dialogDay, setDialogDay] = useState(null);
  const [form, setForm] = useState(EMPTY_ENTRY);
  const [expenses, setExpenses] = useState([]);
  const [expOpen, setExpOpen] = useState(false);
  const [expForm, setExpForm] = useState({ site_id: "", amount: "", description: "", supplier: "", vat_rate: "unknown", file_url: null, name: "" });
  const [expReading, setExpReading] = useState(false);
  useEffect(() => { if (expOpen && !expForm.site_id && sites[0]) { setExpForm((p) => ({ ...p, site_id: sites[0].id })); } }, [expOpen, sites]); // eslint-disable-line react-hooks/exhaustive-deps
  const [expUploading, setExpUploading] = useState(false);
  const [blocking, setBlocking] = useState([]);
  const [mustRead, setMustRead] = useState([]);
  useEffect(() => { api.get("/resources/feed").then((r) => setMustRead((r.data.resources || []).filter((x) => x.must_read && !x.read))).catch(() => {}); }, []);
  const [geo, setGeo] = useState(false);
  useEffect(() => { api.get("/clock/status").then((r) => setGeo(r.data.mode === "geofence")).catch(() => {}); }, []);
  const [vanForm, setVanForm] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/forms/my").then((r) => { setBlocking(r.data.blocking || []); setVanForm((r.data.forms || []).find((f) => !f.done && /\bvans?\b/i.test(f.title)) || null); }).catch(() => {}); }, []);
  const openVanForm = () => nav(`/app/forms/${vanForm.id}?return=/app/timesheets`);
  const tickVan = (checked) => {
    if (checked && vanForm) { toast.info(`Complete "${vanForm.title}" first`); openVanForm(); return; }
    setVanOk(checked);
  };
  const days = useMemo(() => weekDays(monday), [monday]);
  const entrySites = geo ? sites.filter((s) => s.geofence_enabled === false || s.lat == null || s.lng == null) : sites;

  const editable = !ts || ["draft", "returned", "submitted", "rejected"].includes(ts?.status);
  const [editIdx, setEditIdx] = useState(null);
  const [ppeOk, setPpeOk] = useState(false);
  const [vanOk, setVanOk] = useState(false);
  const { employee } = useAuth();
  const isVan = (employee?.categories || []).some((c) => /\bvans?\b/i.test(c));

  const load = useCallback(async () => {
    const [{ data: list }, { data: s }, { data: jt }] = await Promise.all([
      api.get(`/timesheets`, { params: { week_start: monday } }),
      api.get(`/sites`),
      api.get(`/job-types`),
    ]);
    setSites(s); setJobTypes(jt);
    const existing = list.find((t) => t.week_start === monday);
    setTs(existing || null);
    setEntries(existing ? existing.entries.map((e) => ({ ...e })) : []);
  }, [monday]);

  useEffect(() => { load(); }, [load]);

  // live preview
  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/timesheets/calc", { week_start: monday, entries }).then((r) => setComputed(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [entries, monday]);

  const siteName = (id) => sites.find((s) => s.id === id)?.name || "Site";

  const loadExpenses = useCallback(() => api.get("/expenses", { params: { week_start: monday } }).then((r) => setExpenses(r.data)).catch(() => {}), [monday]);
  useEffect(() => { loadExpenses(); }, [loadExpenses]);
  const expUpload = async (ev) => {
    let f = ev.target.files?.[0]; ev.target.value = ""; if (!f) return;
    setExpUploading(true);
    try {
      if (f.type.startsWith("image/")) f = await shrinkPhoto(f);
      if (f.size > 10 * 1024 * 1024) { toast.error("File must be under 10MB"); return; }
      const fd = new FormData(); fd.append("file", f);
      const res = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setExpForm((p) => ({ ...p, file_url: res.data.file_url, name: f.name, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null }));
      setExpUploading(false);
      setExpReading(true);
      try {
        const { data: r } = await api.post("/expenses/read-receipt", { file_url: res.data.file_url });
        setExpForm((p) => ({ ...p, amount: r.amount ?? p.amount, supplier: r.supplier ?? p.supplier, description: p.description || r.description || "", vat_rate: r.vat_rate != null ? String(r.vat_rate) : p.vat_rate, receipt_date: r.receipt_date || p.receipt_date, read_by_ai: true }));
        toast.success("Receipt read. Please check the details before submitting");
      } catch (e) { setExpForm((p) => ({ ...p, read_by_ai: false })); toast.message(formatApiError(e.response?.data?.detail) || "Couldn't read the receipt. Please fill in the details"); }
      finally { setExpReading(false); }
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setExpUploading(false); }
  };
  const addExpense = async () => {
    if (!expForm.site_id) { toast.error("Select the site/job this expense relates to"); return; }
    if (!(Number(expForm.amount) > 0)) { toast.error("Enter an amount"); return; }
    try { await api.post("/expenses", { site_id: expForm.site_id, amount: Number(expForm.amount), description: expForm.description, supplier: expForm.supplier || null, vat_rate: expForm.vat_rate === "unknown" || expForm.vat_rate == null ? null : Number(expForm.vat_rate), read_by_ai: !!expForm.read_by_ai, receipt_date: expForm.receipt_date || null, file_url: expForm.file_url, week_start: monday }); toast.success("Expense submitted for approval"); setExpOpen(false); setExpForm({ site_id: "", amount: "", description: "", supplier: "", vat_rate: "unknown", file_url: null, name: "", preview: null }); loadExpenses(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delExpense = async (id) => { try { await api.delete(`/expenses/${id}`); toast.success("Expense removed"); loadExpenses(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const openAdd = (dayIso) => { setEditIdx(null); setDialogDay(dayIso); setForm({ ...EMPTY_ENTRY, site_id: entrySites[0]?.id || "__other__" }); };
  const openEdit = (idx) => {
    const e = entries[idx]; if (!e) return;
    setEditIdx(idx); setDialogDay(e.date);
    setForm({ ...EMPTY_ENTRY, ...e, site_id: e.other_site ? "__other__" : e.site_id, other_site_name: e.other_site?.name || "", other_site_address: e.other_site?.address || "" });
  };
  const addEntry = () => {
    const isOther = form.site_id === "__other__";
    if (!form.site_id) { toast.error("Select a site"); return; }
    if (isOther && !(form.other_site_name || "").trim()) { toast.error("Enter the site name"); return; }
    if (!form.description.trim()) { toast.error("Add a description of the work completed"); return; }
    const site = sites.find((s) => s.id === form.site_id);
    const { other_site_name, other_site_address, ...rest } = form;
    const extra = isOther ? { site_id: null, site_name: `Other: ${other_site_name.trim()}`, other_site: { name: other_site_name.trim(), address: (other_site_address || "").trim() } } : {};
    const row = { ...rest, ...extra, date: dialogDay, customer_id: site?.customer_id, travel_minutes: Number(form.travel_minutes) || 0, unpaid_break_minutes: Number(form.unpaid_break_minutes) || 0 };
    if (!isOther) { delete row.other_site; if (row.site_name?.startsWith("Other: ")) delete row.site_name; }
    setEntries((prev) => (editIdx === null ? [...prev, row] : prev.map((x, i) => (i === editIdx ? row : x))));
    setEditIdx(null);
    setDialogDay(null);
  };
  const removeEntry = (idx) => setEntries((prev) => prev.filter((_, i) => i !== idx));

  const copyPrevDay = (dayIso) => {
    const idx = days.findIndex((d) => d.iso === dayIso);
    if (idx <= 0) return;
    const prevIso = days[idx - 1].iso;
    const prevEntries = entries.filter((e) => e.date === prevIso);
    if (prevEntries.length === 0) { toast.error("No entries to copy"); return; }
    setEntries((prev) => [...prev, ...prevEntries.map((e) => ({ ...e, date: dayIso }))]);
  };

  const save = async () => {
    try { const { data } = await api.post("/timesheets", { week_start: monday, entries }); setTs(data); toast.success(data.status === "submitted" ? "Changes saved" : "Saved as draft"); return data; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submit = async () => {
    const saved = await save();
    const id = saved?.id || ts?.id;
    if (!id) return;
    try { const { data } = await api.post(`/timesheets/${id}/submit`, { ppe_confirmed: ppeOk, van_checks_confirmed: isVan ? vanOk : undefined }); toast.success("Submitted for approval"); if (data.warnings?.length) data.warnings.forEach((w) => toast.warning(w)); setPpeOk(false); setVanOk(false); load(); }
    catch (e) { const d = e.response?.data?.detail; if (d?.code === "forms_required") setBlocking(d.forms); if (d?.code === "reading_required") setMustRead(d.resources); toast.error(formatApiError(d)); }
  };

  const dayEntries = (iso) => (computed ? computed.entries : entries).filter((e) => e.date === iso);
  const dayHours = (iso) => dayEntries(iso).reduce((s, e) => s + (e.paid_hours || 0), 0);
  const dayBreak = (iso) => dayEntries(iso).reduce((s, e) => s + (e.auto_break_deducted || 0), 0);

  return (
    <EmployeeLayout title="Timesheets">
      <div className="flex items-center justify-between mb-4">
        <button data-testid="week-prev" onClick={() => setMonday(shiftWeek(monday, -1))} className="p-2 border border-zinc-200 rounded-md bg-white"><ChevronLeft className="h-4 w-4" /></button>
        <div className="text-center">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Week of</div>
          <div className="num font-bold">{days[0].dom}/{monday.slice(5, 7)} – {days[6].dom}/{days[6].iso.slice(5, 7)}</div>
        </div>
        <button data-testid="week-next" onClick={() => setMonday(shiftWeek(monday, 1))} className="p-2 border border-zinc-200 rounded-md bg-white"><ChevronRight className="h-4 w-4" /></button>
      </div>

      <div className="flex items-center justify-between mb-3">
        <StatusBadge status={ts?.status || "draft"} />
        {computed && <div className="num text-sm font-bold text-right" data-testid="week-totals">Week total: {hrs(computed.total_paid_hours)}h{(computed.total_ot15_hours > 0 || computed.total_ot2_hours > 0 || computed.topup_hours > 0) && <span data-testid="week-ot" className="ml-2 text-amber-700">{hrs(computed.total_basic_hours)}h basic{computed.total_ot15_hours > 0 ? ` · ${hrs(computed.total_ot15_hours)}h at 1.5×` : ""}{computed.total_ot2_hours > 0 ? ` · ${hrs(computed.total_ot2_hours)}h at 2×` : ""}</span>}{computed.topup_hours > 0 && <span data-testid="week-topup" className="block text-[11px] font-normal text-zinc-500">{hrs(computed.topup_hours)}h of longer days used to reach the full week at the normal rate</span>}{computed.total_travel_minutes > 0 && <span data-testid="week-travel" className="ml-2 text-sky-700">+ {fmtTravel(computed.total_travel_minutes)} travel</span>}</div>}
      </div>
      {geo && editable && (
        <div data-testid="ts-geo-note" className="mb-3 text-sm bg-sky-50 border border-sky-200 rounded-md px-3 py-2.5 text-sky-900">Your hours come from geo-fence clock in / out on the <Link to="/app" className="underline font-medium">Home</Link> screen. Submit the week here when you're done.</div>
      )}
      {blocking.length > 0 && editable && (
        <div data-testid="ts-forms-required" className="mb-3 text-sm bg-amber-50 border border-amber-300 rounded-md px-3 py-2.5 text-amber-900">
          <div className="font-semibold flex items-center gap-1.5 mb-1"><ClipboardList className="h-4 w-4" />Complete before submitting your timesheet</div>
          {blocking.map((f) => <Link key={f.id} to={`/app/forms/${f.id}`} data-testid={`ts-form-link-${f.id}`} className="block underline font-medium">{f.title}</Link>)}
        </div>
      )}
      {mustRead.length > 0 && editable && (
        <div data-testid="ts-reading-required" className="mb-3 text-sm bg-rose-50 border border-rose-200 rounded-md px-3 py-2.5 text-rose-900">
          <div className="font-semibold mb-1">Read and confirm before submitting your timesheet</div>
          {mustRead.map((r) => <Link key={r.id} to="/app/resources" data-testid={`ts-read-link-${r.id}`} className="block underline font-medium">{r.title}</Link>)}
        </div>
      )}
      {ts?.status === "returned" && ts?.return_comment && (
        <div className="mb-3 text-sm bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-800">Returned: {ts.return_comment}</div>
      )}

      <div className="space-y-3">
        {days.map((day) => {
          const de = dayEntries(day.iso);
          const brk = dayBreak(day.iso);
          const bh = ((computed || ts)?.bank_holiday_days || []).find((b) => b.date === day.iso);
          const bhWorked = de.some((e) => e.kind !== "leave");
          return (
            <div key={day.iso} className="bg-white border border-zinc-200 rounded-md overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 bg-zinc-50 border-b border-zinc-100">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-sm">{day.label} <span className="num text-zinc-400 font-normal">{day.dom}/{day.iso.slice(5, 7)}</span>
                    {bh && <span data-testid={`bh-badge-${day.label}`} className="ml-2 text-[10px] font-bold uppercase tracking-wide bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full">Bank Holiday</span>}</div>
                  {bh && <div data-testid={`bh-status-${day.label}`} className="text-xs text-rose-700 mt-0.5">{bh.name} · {bhWorked ? "Worked – bank holiday rate" : `Paid day off – ${hrs(bh.paid_hours)}`}</div>}
                </div>
                <div className="flex items-center gap-2">
                  {de.length > 0 && <span className="num text-sm font-bold">{hrs(dayHours(day.iso))}h</span>}
                  {editable && (
                    <>
                      <button data-testid={`copy-prev-${day.label}`} onClick={() => copyPrevDay(day.iso)} className="p-1 text-zinc-400 hover:text-zinc-700"><Copy className="h-4 w-4" /></button>
                      <button data-testid={`add-entry-${day.label}`} onClick={() => openAdd(day.iso)} className="p-1 text-amber-600"><Plus className="h-5 w-5" /></button>
                    </>
                  )}
                </div>
              </div>
              {de.length === 0 ? (
                <div className="px-3 py-3 text-xs text-zinc-400">No entries</div>
              ) : (
                <div className="divide-y divide-zinc-50">
                  {de.map((e, i) => {
                    if (e.kind === "leave") {
                      return (
                        <div key={i} data-testid={`leave-line-${day.label}-${i}`} className="px-3 py-2 flex items-center justify-between bg-sky-50/60">
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate text-sky-800">{e.bank_holiday ? `Bank Holiday – ${e.bh_name}` : e.leave_paid === false ? "Unpaid leave" : "Annual leave"}</div>
                            <div className="text-xs text-sky-600">{e.bank_holiday ? "Paid day off · added automatically" : "Approved leave · added automatically"}</div>
                          </div>
                          <span className="num text-sm font-semibold text-sky-800">{hrs(e.paid_hours || e.hours || 0)}h</span>
                        </div>
                      );
                    }
                    const globalIdx = entries.findIndex((x) => x === entries.filter((y) => y.date === day.iso && y.kind !== "leave")[i]);
                    return (
                      <div key={i} className="px-3 py-2 flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{e.site_name || (e.other_site ? `Other: ${e.other_site.name} (pending)` : siteName(e.site_id))}</div>
                          <div className="num text-xs text-zinc-500">{e.start_time}–{e.finish_time} · {e.job_type}{e.overtime_minutes > 0 && <span data-testid={`entry-ot-${day.label}-${i}`} className="ml-1 text-amber-700 font-semibold">· {hrs(e.overtime_minutes / 60)}h OT</span>}{e.travel_minutes > 0 && <span data-testid={`entry-travel-${day.label}-${i}`} className="ml-1 text-sky-700">· {fmtTravel(e.travel_minutes)} travel</span>}</div>
                          <HoursSplit e={e} tid={`entry-split-${day.label}-${i}`} />
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="num text-sm font-semibold">{hrs(e.paid_hours || 0)}h</span>
                          {editable && e.source !== "geofence" && <button data-testid={`edit-entry-${day.label}-${i}`} title="Edit" onClick={() => openEdit(globalIdx)} className="text-zinc-400 hover:text-slate-900 mr-2"><Pencil className="h-4 w-4" /></button>}
                          {editable && e.source !== "geofence" && <button data-testid={`del-entry-${day.label}-${i}`} onClick={() => removeEntry(globalIdx)} className="text-rose-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                        </div>
                      </div>
                    );
                  })}
                  {brk > 0 && (
                    <div className="px-3 py-1.5 flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50">
                      <Coffee className="h-3.5 w-3.5" /> {brk}-min unpaid break auto-applied
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 bg-white border border-zinc-200 rounded-md overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-zinc-50 border-b border-zinc-100">
          <div className="font-heading font-bold text-sm flex items-center gap-1.5"><Receipt className="h-4 w-4" /> Expenses this week</div>
          <button data-testid="add-expense-btn" onClick={() => { setExpForm({ site_id: sites[0]?.id || "", amount: "", description: "", supplier: "", vat_rate: "unknown", file_url: null, name: "" }); setExpOpen(true); }} className="p-1 text-amber-600"><Plus className="h-5 w-5" /></button>
        </div>
        {expenses.length === 0 ? (
          <div className="px-3 py-3 text-xs text-zinc-400">No expenses added for this week.</div>
        ) : (
          <div className="divide-y divide-zinc-50">
            {expenses.map((x) => (
              <div key={x.id} data-testid={`expense-${x.id}`} className="px-3 py-2 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{eur(x.amount)} · {x.site_name || "Site"}</div>
                  <div className="text-xs text-zinc-500 truncate">{x.description || "—"}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${x.status === "approved" ? "bg-emerald-100 text-emerald-700" : x.status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"}`}>{x.status}</span>
                  {x.status === "pending" && <button data-testid={`del-expense-${x.id}`} onClick={() => delExpense(x.id)} className="text-rose-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editable && (
        <div className="mt-4 bg-white border border-zinc-200 rounded-lg p-3 space-y-2" data-testid="submit-confirmations">
          {isVan && <label className="flex items-start gap-2 text-sm"><input data-testid="confirm-van-checks" type="checkbox" checked={vanOk} onChange={(e) => tickVan(e.target.checked)} className="mt-0.5 h-4 w-4 accent-amber-500" />I confirm my van checks for this week are complete</label>}
          {isVan && vanForm && <button type="button" data-testid="open-van-checklist" onClick={openVanForm} className="ml-6 text-xs font-semibold text-amber-700 underline">{vanForm.title} isn't done for this {vanForm.frequency === "weekly" ? "week" : "period"} yet. Open it now</button>}
          <label className="flex items-start gap-2 text-sm"><input data-testid="confirm-ppe" type="checkbox" checked={ppeOk} onChange={(e) => setPpeOk(e.target.checked)} className="mt-0.5 h-4 w-4 accent-amber-500" />I confirm I have the required PPE and safety equipment</label>
          {ts?.status === "submitted" && <p className="text-[11px] text-zinc-500">Already submitted. You can still correct times until it's approved. Save, or submit again to send the changes.</p>}
        </div>
      )}
      {editable && (
        <div className="sticky bottom-16 mt-4 flex gap-2 bg-[#FAFAFA] py-2">
          <Button data-testid="save-draft-btn" variant="outline" onClick={save} className="flex-1 h-12">{ts?.status === "submitted" ? "Save changes" : "Save draft"}</Button>
          <Button data-testid="submit-week-btn" disabled={!ppeOk || (isVan && !vanOk)} onClick={submit} className="flex-1 h-12 bg-amber-500 hover:bg-amber-600 text-black font-bold">{ts?.status === "submitted" ? "Resubmit week" : "Submit week"}</Button>
        </div>
      )}

      {/* Add entry dialog */}
      <Dialog open={!!dialogDay} onOpenChange={(o) => { if (!o) { setDialogDay(null); setEditIdx(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editIdx === null ? "Add site time" : "Edit site time"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Site</Label>
              <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                <SelectTrigger data-testid="entry-site" className="h-12 mt-1"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent>{entrySites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}<SelectItem value="__other__">Other site (not listed)…</SelectItem></SelectContent>
              </Select>
              {form.site_id === "__other__" && <div className="grid gap-2 mt-2">
                <Input data-testid="entry-other-name" placeholder="Site name" value={form.other_site_name || ""} onChange={(e) => setForm({ ...form, other_site_name: e.target.value })} />
                <Input data-testid="entry-other-address" placeholder="Address (optional)" value={form.other_site_address || ""} onChange={(e) => setForm({ ...form, other_site_address: e.target.value })} />
                <p className="text-[11px] text-amber-700">Your admin will check this site before approving.</p>
              </div>}
              <Select value={form.job_type} onValueChange={(v) => setForm({ ...form, job_type: v })}>
                <SelectTrigger data-testid="entry-jobtype" className="h-12 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{jobTypes.map((j) => <SelectItem key={j.id} value={j.name}>{j.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start</Label><Input data-testid="entry-start" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="h-12 mt-1" /></div>
              <div><Label>Finish</Label><Input data-testid="entry-finish" type="time" value={form.finish_time} onChange={(e) => setForm({ ...form, finish_time: e.target.value })} className="h-12 mt-1" /></div>
            </div>
            <div>
              <Label>Travel time</Label>
              <div className="grid grid-cols-2 gap-3 mt-1">
                <Select value={String(Math.floor((form.travel_minutes || 0) / 60))} onValueChange={(v) => setForm({ ...form, travel_minutes: Number(v) * 60 + ((form.travel_minutes || 0) % 60) })}>
                  <SelectTrigger data-testid="entry-travel-hours" className="h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>{TRAVEL_H.map((h) => <SelectItem key={h} value={String(h)}>{h} hr{h === 1 ? "" : "s"}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={String((form.travel_minutes || 0) % 60)} onValueChange={(v) => setForm({ ...form, travel_minutes: Math.floor((form.travel_minutes || 0) / 60) * 60 + Number(v) })}>
                  <SelectTrigger data-testid="entry-travel-mins" className="h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>{TRAVEL_M.map((m) => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">Overtime is worked out automatically from your hours.</p>
            </div>
            <div><Label>Description <span className="text-rose-500">*</span></Label><Textarea data-testid="entry-desc" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Work completed…" /></div>
            <Button data-testid="entry-add-btn" onClick={addEntry} className="w-full h-12 bg-slate-900 text-white">{editIdx === null ? "Add to day" : "Save changes"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add expense dialog */}
      <Dialog open={expOpen} onOpenChange={(o) => !o && setExpOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add an expense</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Site / job this expense relates to</Label>
              <Select value={expForm.site_id} onValueChange={(v) => setExpForm({ ...expForm, site_id: v })}>
                <SelectTrigger data-testid="expense-site" className="h-12 mt-1"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Amount paid € (inc VAT)</Label><Input data-testid="expense-amount" type="number" step="0.01" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} className="h-12 mt-1" placeholder="0.00" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Supplier</Label><Input data-testid="expense-supplier" value={expForm.supplier || ""} onChange={(e) => setExpForm({ ...expForm, supplier: e.target.value })} className="h-12 mt-1" placeholder="e.g. Chadwicks" /></div>
              <div><Label>VAT rate</Label>
                <Select value={String(expForm.vat_rate ?? "unknown")} onValueChange={(v) => setExpForm({ ...expForm, vat_rate: v })}>
                  <SelectTrigger data-testid="expense-vat" className="h-12 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="unknown">Not shown</SelectItem><SelectItem value="23">23%</SelectItem><SelectItem value="13.5">13.5%</SelectItem><SelectItem value="0">0%</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Receipt date</Label><Input data-testid="expense-receipt-date" type="date" value={expForm.receipt_date || ""} onChange={(e) => setExpForm({ ...expForm, receipt_date: e.target.value })} className="h-12 mt-1" /></div>
            <div><Label>Description</Label><Textarea data-testid="expense-desc" value={expForm.description} onChange={(e) => setExpForm({ ...expForm, description: e.target.value })} placeholder="What was this for?" /></div>
            <div>
              <Label>Receipt (optional)</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <label data-testid="expense-camera-btn" className="flex items-center justify-center gap-2 h-12 px-3 rounded-md cursor-pointer text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors">
                  <Camera className="h-4 w-4" />Take photo
                  <input data-testid="expense-camera" type="file" accept="image/*" capture="environment" onChange={expUpload} className="hidden" />
                </label>
                <label data-testid="expense-file-btn" className="flex items-center justify-center gap-2 h-12 px-3 border border-dashed border-zinc-300 rounded-md cursor-pointer text-sm text-zinc-600 hover:border-amber-400 transition-colors">
                  <Paperclip className="h-4 w-4" />Choose file
                  <input data-testid="expense-file" type="file" accept="image/*,application/pdf" onChange={expUpload} className="hidden" />
                </label>
              </div>
              {expUploading && <div className="text-xs text-zinc-500 mt-2" data-testid="expense-uploading">Uploading receipt…</div>}
              {expReading && <div className="text-xs text-amber-700 mt-2 animate-pulse" data-testid="expense-reading">Reading receipt…</div>}
              {!expReading && expForm.read_by_ai && <div className="text-xs text-sky-700 mt-2" data-testid="expense-read-note">Filled in from the receipt. Please check the amount and supplier.</div>}
              {!expUploading && expForm.file_url && (
                <div className="flex items-center gap-3 mt-2 p-2 border border-zinc-200 rounded-md" data-testid="expense-receipt-attached">
                  {expForm.preview ? <img src={expForm.preview} alt="Receipt" className="h-14 w-14 object-cover rounded" /> : <Paperclip className="h-5 w-5 text-zinc-400" />}
                  <span className="text-xs text-zinc-600 truncate flex-1">{expForm.name}</span>
                  <button data-testid="expense-receipt-remove" onClick={() => setExpForm((p) => { if (p.preview) URL.revokeObjectURL(p.preview); return { ...p, file_url: null, name: "", preview: null, read_by_ai: false }; })} className="text-rose-500 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
            <Button data-testid="expense-submit" disabled={expUploading || expReading} onClick={addExpense} className="w-full h-12 bg-amber-500 text-black font-bold">Submit expense</Button>
          </div>
        </DialogContent>
      </Dialog>
    </EmployeeLayout>
  );
}
